import { Loader2, Send, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { STATES, STATE_LABELS, TENANT_BADGES } from './customerConstants';

export function StateChangeModal({ open, onOpenChange, newState, setNewState, stateNote, setStateNote, saving, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
        <DialogHeader>
          <DialogTitle style={{ color: 'var(--text-primary)' }} className="font-['Manrope']">Update Onboarding State</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4 mt-2">
          <div>
            <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-faint)' }}>New State</label>
            <select value={newState} onChange={e => setNewState(e.target.value)} required className="w-full select-slate">
              <option value="">Select state...</option>
              {[...STATES, 'DECLINED'].map(s => <option key={s} value={s}>{STATE_LABELS[s] || s.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-faint)' }}>Note (optional)</label>
            <textarea value={stateNote} onChange={e => setStateNote(e.target.value)} rows={3}
              className="w-full input-slate resize-none" placeholder="Add a note..." />
          </div>
          <button type="submit" disabled={saving} data-testid="confirm-state-change-btn"
            className="w-full btn-primary flex items-center justify-center gap-2 text-sm uppercase tracking-wide">
            {saving && <Loader2 size={14} className="animate-spin" />} Confirm
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function PaymentModal({ open, onOpenChange, paymentForm, setPaymentForm, savingPayment, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
        <DialogHeader>
          <DialogTitle style={{ color: 'var(--text-primary)' }} className="font-['Manrope']">Add Payment Method</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-faint)' }}>Type</label>
            <select value={paymentForm.type} onChange={e => setPaymentForm(p => ({ ...p, type: e.target.value }))} className="w-full select-slate">
              {['check', 'ach', 'credit_card', 'wire', 'zelle', 'cash', 'other'].map(t => (
                <option key={t} value={t}>{t.replace(/_/g, ' ').toUpperCase()}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-faint)' }}>Label *</label>
            <input value={paymentForm.label} onChange={e => setPaymentForm(p => ({ ...p, label: e.target.value }))}
              className="w-full input-slate" placeholder="e.g. Company Chase Account" required />
          </div>
          <div>
            <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-faint)' }}>Details</label>
            <textarea value={paymentForm.details} onChange={e => setPaymentForm(p => ({ ...p, details: e.target.value }))}
              rows={2} className="w-full input-slate resize-none" placeholder="Account ending, routing, notes..." />
          </div>
          <button onClick={onSubmit} disabled={savingPayment}
            className="w-full btn-primary flex items-center justify-center gap-2 text-sm uppercase tracking-wide"
            data-testid="confirm-add-payment-btn">
            {savingPayment && <Loader2 size={14} className="animate-spin" />} Add Payment Method
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AddUnitModal({ open, onOpenChange, truckForm, setTruckForm, saving, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
        <DialogHeader>
          <DialogTitle style={{ color: 'var(--text-primary)' }} className="font-['Manrope']">Add Unit</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3 mt-2">
          <div>
            <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-faint)' }}>Unit Type</label>
            <div className="flex gap-3">
              {['Truck', 'Trailer'].map(t => (
                <label key={t} className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" checked={truckForm.unit_type === t} onChange={() => setTruckForm(p => ({ ...p, unit_type: t }))}
                    className="w-4 h-4" style={{ accentColor: 'var(--primary)' }} />
                  <span className="font-['IBM_Plex_Sans'] text-sm" style={{ color: 'var(--text-primary)' }}>{t}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Unit # *', key: 'unit_number', required: true },
              { label: 'VIN', key: 'vin' },
              { label: 'Make *', key: 'make', required: true },
              { label: 'Model *', key: 'model', required: true },
              { label: 'Year', key: 'year', type: 'number' },
              { label: 'Current Mileage', key: 'current_mileage', type: 'number' },
              { label: 'License Plate', key: 'plate' },
            ].map(({ label, key, type, required }) => (
              <div key={key}>
                <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1" style={{ color: 'var(--text-faint)' }}>{label}</label>
                <input type={type || 'text'} required={required} value={truckForm[key]} onChange={e => setTruckForm(p => ({ ...p, [key]: e.target.value }))}
                  className="w-full input-slate" />
              </div>
            ))}
          </div>
          <button type="submit" disabled={saving} data-testid="add-truck-submit"
            className="w-full btn-primary flex items-center justify-center gap-2 text-sm uppercase tracking-wide">
            {saving && <Loader2 size={14} className="animate-spin" />} Add Unit
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ShareHealthModal({ open, onOpenChange, healthScore, customer, shareMsg, setShareMsg, sharing, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
        <DialogHeader>
          <DialogTitle style={{ color: 'var(--text-primary)' }} className="font-['Manrope']">Share Fleet Health Report</DialogTitle>
        </DialogHeader>
        {healthScore && (
          <div className="space-y-4 mt-2">
            <div className="p-4 rounded-md text-center" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
              <p className={`text-4xl font-['JetBrains_Mono'] font-bold mb-1 ${
                healthScore.color === 'green' ? 'text-[var(--success)]' :
                healthScore.color === 'teal' ? 'text-teal-400' :
                healthScore.color === 'yellow' ? 'text-[var(--warning)]' :
                healthScore.color === 'orange' ? 'text-orange-400' : 'text-[var(--error)]'
              }`}>{healthScore.score}/100</p>
              <p className="font-['Manrope'] font-bold text-sm" style={{ color: 'var(--text-primary)' }}>{healthScore.label}</p>
              <p className="text-xs font-['IBM_Plex_Sans'] mt-1" style={{ color: 'var(--text-faint)' }}>Will be sent to: {customer?.contact_email}</p>
            </div>
            <div>
              <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-faint)' }}>Personal Message (optional)</label>
              <textarea value={shareMsg} onChange={e => setShareMsg(e.target.value)} rows={3}
                className="w-full input-slate resize-none" placeholder="e.g. Great progress this month!" />
            </div>
            <button onClick={onSubmit} disabled={sharing} data-testid="send-health-report-btn"
              className="w-full btn-primary flex items-center justify-center gap-2 text-sm uppercase tracking-wide">
              {sharing ? <><Loader2 size={14} className="animate-spin" /> Sending...</> : <><Send size={14} /> Send Report</>}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function TenantModal({ open, onOpenChange, customer, tenantChanging, tenantReason, setTenantReason, onChangeTenant }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
        <DialogHeader>
          <DialogTitle style={{ color: 'var(--text-primary)' }} className="font-['Manrope']">Change Customer Tenant</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="p-4 rounded-md" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
            <p className="text-sm font-['IBM_Plex_Sans']" style={{ color: 'var(--text-muted)' }}>
              Current tenant: <span className={`font-bold px-2 py-0.5 rounded ${TENANT_BADGES[customer?.tenant_group]?.color || ''}`}>
                {customer?.tenant_group || 'N/A'}
              </span>
            </p>
          </div>
          <div>
            <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-2" style={{ color: 'var(--text-faint)' }}>Move to:</label>
            <div className="flex gap-3">
              <button onClick={() => onChangeTenant('MM')} disabled={tenantChanging || customer?.tenant_group === 'MM'}
                className="flex-1 py-3 rounded-md font-['IBM_Plex_Sans'] font-bold text-sm transition-colors disabled:opacity-40"
                style={{ background: customer?.tenant_group === 'MM' ? 'var(--border)' : 'rgb(16, 185, 129)', color: 'white' }}
                data-testid="move-to-mm-btn">
                {tenantChanging ? <Loader2 size={14} className="animate-spin mx-auto" /> : 'MM (Millennials)'}
              </button>
              <button onClick={() => onChangeTenant('MT')} disabled={tenantChanging || customer?.tenant_group === 'MT'}
                className="flex-1 py-3 rounded-md font-['IBM_Plex_Sans'] font-bold text-sm transition-colors disabled:opacity-40"
                style={{ background: customer?.tenant_group === 'MT' ? 'var(--border)' : 'rgb(139, 92, 246)', color: 'white' }}
                data-testid="move-to-mt-btn">
                {tenantChanging ? <Loader2 size={14} className="animate-spin mx-auto" /> : 'MT (Tenant)'}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-faint)' }}>Reason for change (optional)</label>
            <textarea value={tenantReason} onChange={e => setTenantReason(e.target.value)} rows={2}
              className="w-full input-slate resize-none" placeholder="e.g. Acquired by MT company..." />
          </div>
          <div className="p-3 rounded-md flex items-start gap-2" style={{ background: 'var(--warning-muted)', border: '1px solid var(--warning)' }}>
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" style={{ color: 'var(--warning)' }} />
            <p className="text-xs font-['IBM_Plex_Sans']" style={{ color: 'var(--warning)' }}>
              This action is audited. All associated data (trucks, repairs, invoices) will be moved to the new tenant.
            </p>
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TierChangeModal({ open, onOpenChange, customer, customerTier, pricingTiers, selectedTierId, setSelectedTierId, tierConfirmText, setTierConfirmText, tierChanging, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setTierConfirmText(''); setSelectedTierId(''); } }}>
      <DialogContent style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
        <DialogHeader>
          <DialogTitle style={{ color: 'var(--text-primary)' }} className="font-['Manrope']">Change Pricing Tier</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="p-4 rounded-md" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
            <p className="text-sm font-['IBM_Plex_Sans']" style={{ color: 'var(--text-muted)' }}>
              Current tier: <span className="font-bold font-['JetBrains_Mono'] px-2 py-0.5 rounded text-amber-400 bg-amber-900/20" data-testid="current-tier-display">
                {customerTier?.name || 'None'} ({customerTier?.markup_percent || 0}%)
              </span>
            </p>
          </div>
          <div>
            <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-faint)' }}>New Tier</label>
            <select value={selectedTierId} onChange={e => setSelectedTierId(e.target.value)}
              className="w-full select-slate" data-testid="tier-change-select">
              <option value="">Select tier...</option>
              {pricingTiers.filter(t => t.id !== customer?.pricing_tier_id).map(t => (
                <option key={t.id} value={t.id}>{t.name} — {t.markup_percent}% markup</option>
              ))}
            </select>
          </div>
          {selectedTierId && (() => {
            const newTier = pricingTiers.find(t => t.id === selectedTierId);
            if (!newTier) return null;
            return (
              <div className="p-3 rounded-md" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-2" style={{ color: 'var(--text-faint)' }}>Price Impact (on $100 base)</p>
                <div className="flex items-center justify-between text-xs font-['IBM_Plex_Sans']">
                  <span style={{ color: 'var(--text-muted)' }}>Old: ${(100 * (1 + (customerTier?.markup_percent || 0) / 100)).toFixed(2)}</span>
                  <span style={{ color: 'var(--text-faint)' }}>→</span>
                  <span className="font-bold font-['JetBrains_Mono']" style={{ color: 'var(--warning)' }}>New: ${(100 * (1 + newTier.markup_percent / 100)).toFixed(2)}</span>
                </div>
              </div>
            );
          })()}
          <div>
            <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-widest mb-1.5" style={{ color: 'var(--error)' }}>Type CONFIRM to proceed</label>
            <input value={tierConfirmText} onChange={e => setTierConfirmText(e.target.value)}
              placeholder="Type CONFIRM" className="w-full input-slate" data-testid="tier-confirm-input" />
          </div>
          <div className="p-3 rounded-md flex items-start gap-2" style={{ background: 'var(--warning-muted)', border: '1px solid var(--warning)' }}>
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" style={{ color: 'var(--warning)' }} />
            <p className="text-xs font-['IBM_Plex_Sans']" style={{ color: 'var(--warning)' }}>
              This affects all future quotes and invoices for this customer. This action is audited.
            </p>
          </div>
          <button onClick={onSubmit} disabled={tierChanging || !selectedTierId || tierConfirmText !== 'CONFIRM'}
            className="w-full btn-primary flex items-center justify-center gap-2 text-sm uppercase tracking-wide disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="confirm-tier-change-btn">
            {tierChanging && <Loader2 size={14} className="animate-spin" />}
            {tierChanging ? 'Updating...' : 'Change Pricing Tier'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
